type PageLike = {
  title?: string | null;
  path?: string | null;
  url?: string | null;
  h1?: string | null;
  metaTitle?: string | null;
  slug?: string | null;
};

type SiteLike = {
  clientName?: string | null;
  displayName?: string | null;
  title?: string | null;
  domain?: string | null;
};

const BOILERPLATE_SEPARATORS = [" | ", " - ", " \u2013 ", " \u2014 ", " :: "];

export function pageDisplayLabel(
  page: PageLike,
  site?: SiteLike | null,
): string {
  const siteNames = siteNameSet(site);
  const meaningfulTitle = firstMeaningful(
    [page.title, page.h1, page.metaTitle],
    siteNames,
  );
  if (meaningfulTitle) {
    return meaningfulTitle;
  }

  const fromPath = humanisePath(page.path ?? pathFromUrl(page.url));
  if (fromPath) {
    return fromPath;
  }

  const fromSlug = humaniseSegment(page.slug);
  if (fromSlug) {
    return fromSlug;
  }

  return page.path ?? page.url ?? "Untitled page";
}

export function rawPagePath(page: PageLike): string {
  return page.path ?? pathFromUrl(page.url) ?? page.url ?? "Unknown path";
}

function firstMeaningful(
  values: Array<string | null | undefined>,
  siteNames: Set<string>,
): string | null {
  for (const value of values) {
    const cleaned = cleanTitle(value);
    if (!cleaned || siteNames.has(normalise(cleaned))) {
      continue;
    }
    const withoutBoilerplate = stripBoilerplate(cleaned, siteNames);
    if (withoutBoilerplate && !siteNames.has(normalise(withoutBoilerplate))) {
      return withoutBoilerplate;
    }
  }
  return null;
}

function siteNameSet(site?: SiteLike | null): Set<string> {
  return new Set(
    [site?.displayName, site?.clientName, site?.title, site?.domain]
      .map(cleanTitle)
      .filter((value): value is string => Boolean(value))
      .map(normalise),
  );
}

function cleanTitle(value: string | null | undefined): string | null {
  const cleaned = value?.replace(/\s+/g, " ").trim();
  return cleaned && cleaned.length > 0 ? cleaned : null;
}

function stripBoilerplate(
  value: string,
  siteNames: Set<string>,
): string | null {
  for (const separator of BOILERPLATE_SEPARATORS) {
    const parts = value
      .split(separator)
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length < 2) {
      continue;
    }
    const candidate = parts.find((part) => !siteNames.has(normalise(part)));
    if (candidate) {
      return candidate;
    }
  }
  return value;
}

function humanisePath(path: string | null): string | null {
  const clean = path?.split("?")[0]?.replace(/\/+$/, "") ?? "";
  if (!clean || clean === "/") {
    return "Home";
  }
  const parts = clean.split("/").filter(Boolean);
  const last = parts.at(-1);
  if (!last) {
    return null;
  }
  const label = humaniseSegment(last);
  if (!label) {
    return null;
  }
  if (parts.at(-2) === "policies" && !/\bpolicy\b/i.test(label)) {
    return `${label} Policy`;
  }
  return label;
}

function humaniseSegment(value: string | null | undefined): string | null {
  const cleaned = value
    ?.split("?")[0]
    ?.replace(/\.[a-z0-9]+$/i, "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) {
    return null;
  }
  return cleaned
    .split(" ")
    .map((word) =>
      /^[A-Z0-9]+$/.test(word)
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
}

function pathFromUrl(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }
  try {
    return new URL(url).pathname;
  } catch {
    return null;
  }
}

function normalise(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}
